import { useTerminology } from '@/lib/useTerminology'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'
import type { ModuleKey } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { formCard, pageTitle, sectionTitle, fieldLabel, inputBase, pageWide } from '@/lib/form-styles'
import {
  FeldStatus,
  FeldStatusZeichen,
  SchalterZeile,
  feldKlasse,
  useAenderung,
  useFeld,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'
import { useZeitKonfiguration } from '@/lib/zeit-konfiguration'
import { timeConfigApi } from '../api/time-tracking.api'
import type { TimeEntryMode, EditPolicy, StandardFieldDefinition } from '../types/time-tracking.types'

/**
 * AP-29-N3-C — Ein gesperrter Schalter wird erklaert, nicht angeboten.
 *
 * Bis hierher liess sich alles ankreuzen; die Ablehnung kam beim Speichern, mit
 * dem Modulschluessel als Begruendung. Der Hinweis nennt stattdessen den
 * **Bausteinnamen** und den Weg zur Buchung — die beiden Auskuenfte, mit denen
 * ein Administrator etwas anfangen kann.
 */
function BausteinSperre({ baustein }: { baustein: ModuleKey }) {
  const { t } = useTranslation()
  return (
    <p className="ml-7 flex items-center gap-1.5 text-xs text-muted-foreground">
      <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span>
        Gehört zu <span className="font-medium text-foreground">{t(`modules.names.${baustein}`)}</span> —
        für diesen Betrieb nicht gebucht.{' '}
        <Link to="/settings/modules" className="underline underline-offset-2 hover:text-foreground">
          Module &amp; Buchungen
        </Link>
      </span>
    </p>
  )
}

/**
 * AP-36 Phase C — Grundeinstellungen der Zeiterfassung, **feldweise ohne Knopf**:
 * Schalter und Auswahl sofort, Zahlen und Texte beim Verlassen. Früher speicherte
 * die Seite 700 ms nach jeder Änderung die ganze Konfiguration (S-2).
 */
export default function TimeConfigPage() {
  const { stelle, geladen, fehler } = useZeitKonfiguration()
  const [definitionen, setDefinitionen] = useState<StandardFieldDefinition[]>([])

  useEffect(() => {
    timeConfigApi
      .getStandardFields()
      .then(({ data }) => setDefinitionen(Array.isArray(data) ? data : []))
      .catch(() => setDefinitionen([]))
  }, [])

  if (fehler) {
    return (
      <div className={pageWide}>
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">{fehler}</div>
      </div>
    )
  }
  if (!geladen) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Wird geladen...</p>
      </div>
    )
  }
  return <Grundeinstellungen stelle={stelle as Speicherstelle<unknown>} definitionen={definitionen} />
}

const MODI: { key: TimeEntryMode; label: string }[] = [
  { key: 'CLOCK', label: 'Stempeluhr' },
  { key: 'MANUAL', label: 'Manuelle Erfassung' },
]

/** KA-45: eine Schwelle der Verdachtsprüfung — Zahl mit Einheit. */
function Schwelle({ feld, id, label, einheit }: { feld: ReturnType<typeof useFeld<number | null>>; id: string; label: string; einheit: string }) {
  return (
    <div className="space-y-1.5">
      <label className={fieldLabel} htmlFor={id}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input id={id} inputMode="numeric" className={`${inputBase} max-w-[8rem]`} {...feld.props} />
        <span className="text-sm text-muted-foreground">{einheit}</span>
        <FeldStatusZeichen status={feld} />
      </div>
      <FeldStatus status={feld} zeichen={false} />
    </div>
  )
}

function Grundeinstellungen({ stelle, definitionen }: { stelle: Speicherstelle<unknown>; definitionen: StandardFieldDefinition[] }) {
  const hasModule = useAuthStore((s) => s.hasModule)
  const hatGps = hasModule('TIME_TRACKING.GPS')
  const hatScan = hasModule('TIME_TRACKING.SCAN')

  const modi = useFeld<TimeEntryMode[]>(stelle, 'allowedModes', {
    art: 'auswahl',
    label: 'Erlaubte Modi',
    lies: (t) => JSON.parse(t) as TimeEntryMode[],
    schreib: (w) => JSON.stringify(Array.isArray(w) ? w : ['CLOCK', 'MANUAL']),
    pruefe: (w) => (w.length === 0 ? 'Mindestens ein Modus muss aktiv bleiben.' : null),
  })
  const notizen = useFeld<boolean>(stelle, 'allowNotes', { art: 'schalter', label: 'Notizen erlauben' })
  const pausen = useFeld<boolean>(stelle, 'breakMinutesEnabled', { art: 'schalter', label: 'Pausenminuten-Feld' })
  const luecke = useFeld<number | null>(stelle, 'breakGapMaxMinutes', {
    art: 'zahl',
    stellen: 0,
    label: 'Lücken als Pause bis',
    pruefe: (w) => (w !== null && (!Number.isInteger(w) || w < 0 || w > 1440) ? 'Ganze Minuten zwischen 0 und 1440.' : null),
  })
  // Abhängige Schalter gehen als Folgefelder im selben PATCH mit (3.1).
  const gps = useFeld<boolean>(stelle, 'gpsEnabled', {
    art: 'schalter',
    label: 'GPS-Erfassung',
    gesperrt: !hatGps,
    folge: (an) => (an ? {} : { gpsRequired: false, gpsAllowBypass: false }),
  })
  const gpsPflicht = useFeld<boolean>(stelle, 'gpsRequired', {
    art: 'schalter',
    label: 'GPS erzwingen',
    folge: (an) => (an ? {} : { gpsAllowBypass: false }),
  })
  const gpsBypass = useFeld<boolean>(stelle, 'gpsAllowBypass', { art: 'schalter', label: 'GPS-Bypass' })
  const qr = useFeld<boolean>(stelle, 'qrRequired', {
    art: 'schalter',
    label: 'QR-Code-Scan erzwingen',
    gesperrt: !hatScan,
    folge: (an) => (an ? {} : { qrAllowBypass: false }),
  })
  const qrBypass = useFeld<boolean>(stelle, 'qrAllowBypass', { art: 'schalter', label: 'QR-Bypass' })
  const qrErsterLetzter = useFeld<boolean>(stelle, 'qrFirstLastOnly', { art: 'schalter', label: 'Nur erster und letzter Stempel' })
  const richtlinie = useFeld<EditPolicy>(stelle, 'editPolicy', {
    art: 'auswahl',
    label: 'Richtlinientyp',
    // Ein Zeitfenster ohne Stunden wäre wirkungslos — beim Umschalten die Vorgabe mitschicken.
    folge: (w) => (w === 'TIME_WINDOW' && stelle.basis('editWindowHours') == null ? { editWindowHours: 24 } : {}),
  })
  const fenster = useFeld<number | null>(stelle, 'editWindowHours', {
    art: 'zahl',
    stellen: 0,
    label: 'Bearbeitungsfenster',
    pflicht: true,
    pruefe: (w) => (w !== null && (!Number.isInteger(w) || w < 1 || w > 168) ? 'Ganze Stunden zwischen 1 und 168.' : null),
  })
  // KA-45: Verdachtsprüfung — ganze Zahlen in festen Grenzen (wie das DTO).
  const ganz = (min: number, max: number, einheit: string) => (w: number | null) =>
    w === null || !Number.isInteger(w) || w < min || w > max ? `Ganze Zahl zwischen ${min} und ${max} ${einheit}.` : null
  const verdacht = useFeld<boolean>(stelle, 'suspicionEnabled', { art: 'schalter', label: 'Verdachtsprüfung' })
  const kurz = useFeld<number | null>(stelle, 'suspicionMinShiftMin', { art: 'zahl', stellen: 0, label: 'Kürzer als', pflicht: true, pruefe: ganz(0, 240, 'Minuten') })
  const lang = useFeld<number | null>(stelle, 'suspicionMaxShiftHours', { art: 'zahl', stellen: 0, label: 'Länger als', pflicht: true, pruefe: ganz(1, 48, 'Stunden') })
  const nachtVon = useFeld<number | null>(stelle, 'suspicionUnusualStart', { art: 'zahl', stellen: 0, label: 'Ungewöhnlich ab', pflicht: true, pruefe: ganz(0, 23, 'Uhr') })
  const nachtBis = useFeld<number | null>(stelle, 'suspicionUnusualEnd', { art: 'zahl', stellen: 0, label: 'Ungewöhnlich bis', pflicht: true, pruefe: ganz(0, 23, 'Uhr') })
  const abweichung = useFeld<number | null>(stelle, 'suspicionGpsMaxDeviation', { art: 'zahl', stellen: 0, label: 'GPS-Abweichung', pflicht: true, pruefe: ganz(10, 100000, 'Meter') })
  const tempo = useFeld<number | null>(stelle, 'suspicionMaxSpeedKmh', { art: 'zahl', stellen: 0, label: 'Unmögliche Wege', pflicht: true, pruefe: ganz(10, 2000, 'km/h') })
  const datenschutzText = useFeld<string | null>(stelle, 'privacyText', { label: 'Datenschutzhinweis' })
  const datenschutzLink = useFeld<string | null>(stelle, 'privacyUrl', {
    label: 'Link zur Datenschutzerklärung',
    pruefe: (w) => (w && !/^https?:\/\/\S+$/i.test(w.trim()) ? 'Bitte eine Adresse mit http:// oder https:// angeben.' : null),
  })

  const aktiveModi = Array.isArray(modi.wert) ? modi.wert : []

  return (
    <div className={pageWide}>
      <div>
        <h1 className={pageTitle}>Grundeinstellungen</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Verhalten der Zeiterfassung für deinen Mandanten. Jede Änderung wird sofort bzw. beim Verlassen des Felds gespeichert.
        </p>
      </div>

      {/* ── Abschnitt 1: Allgemeine Einstellungen ───────────────────────────── */}
      <div className={formCard}>
        <h2 className={`mb-5 ${sectionTitle}`}>Allgemeine Einstellungen</h2>
        <div className="space-y-5">
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Erlaubte Modi <FeldStatusZeichen status={modi} />
            </p>
            <div className="space-y-3">
              {MODI.map(({ key, label }) => {
                const an = aktiveModi.includes(key)
                const letzter = an && aktiveModi.length === 1
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-3 ${letzter ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    title={letzter ? 'Mindestens ein Modus muss ausgewählt bleiben.' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={an}
                      disabled={letzter}
                      onChange={() => modi.setze(an ? aktiveModi.filter((m) => m !== key) : [...aktiveModi, key])}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-ring disabled:cursor-not-allowed"
                    />
                    <span className="text-sm text-foreground">{label}</span>
                  </label>
                )
              })}
            </div>
            <FeldStatus status={modi} zeichen={false} />
          </div>

          <SchalterZeile feld={notizen} label="Notizen erlauben" />
          <SchalterZeile feld={pausen} label="Pausenminuten-Feld in den Buchungsmasken anzeigen" />

          {/* AP-24: Lücken zwischen zwei Buchungen als Pause werten */}
          <div className="space-y-1.5">
            <label className={fieldLabel} htmlFor="breakGapMax">
              Lücken zwischen Buchungen als Pause werten bis
            </label>
            <div className="flex items-center gap-2">
              <input id="breakGapMax" inputMode="numeric" placeholder="ohne Grenze" className={`${inputBase} max-w-[10rem]`} {...luecke.props} />
              <span className="text-sm text-muted-foreground">Minuten</span>
              <FeldStatusZeichen status={luecke} />
            </div>
            <FeldStatus status={luecke} zeichen={false} />
            <p className="text-xs text-muted-foreground">
              Stempelt jemand 08:00–10:00 und 10:30–16:00, gelten die 30 Minuten dazwischen als
              Pause. Leer lassen, damit jede Lücke als Pause zählt. Bei geteilten Diensten (vormittags
              und abends) verhindert eine Grenze, dass der halbe Tag als Pause erscheint — Lücken
              darüber zählen weder als Pause noch als Anwesenheit.
            </p>
          </div>

          <SchalterZeile feld={gps} label="GPS-Erfassung aktivieren" aus={!hatGps} />
          {!hatGps && <BausteinSperre baustein="TIME_TRACKING.GPS" />}
          {hatGps && gps.wert && (
            <SchalterZeile feld={gpsPflicht} einzug={1} label="GPS erzwingen" hinweis="Einstempeln ist nur möglich wenn GPS-Berechtigung erteilt wurde." />
          )}
          {hatGps && gps.wert && gpsPflicht.wert && (
            <SchalterZeile
              feld={gpsBypass}
              einzug={2}
              label="GPS-Bypass erlauben"
              hinweis="Mitarbeiter dürfen GPS überspringen, müssen aber eine Begründung eingeben. Der Eintrag wird markiert."
            />
          )}

          <SchalterZeile feld={qr} label="QR-Code-Scan erzwingen" hinweis="Stempeln erfordert einen gültigen QR-Code am Standort." aus={!hatScan} />
          {!hatScan && <BausteinSperre baustein="TIME_TRACKING.SCAN" />}
          {hatScan && qr.wert && (
            <>
              <SchalterZeile
                feld={qrBypass}
                einzug={1}
                label="QR-Bypass erlauben"
                hinweis="Mitarbeiter dürfen den QR-Scan überspringen, müssen aber eine Begründung eingeben. Der Eintrag wird markiert."
              />
              <SchalterZeile
                feld={qrErsterLetzter}
                einzug={1}
                label="Nur erster und letzter Stempel"
                hinweis="QR-Scan nur beim ersten Einstempeln und letzten Ausstempeln des Tages — nicht bei jeder Buchung."
              />
            </>
          )}
        </div>
      </div>

      {/* ── KA-45: Verdachtsprüfung ─────────────────────────────────────────── */}
      <div className={formCard}>
        <h2 className={`mb-2 ${sectionTitle}`}>Verdachtsprüfung</h2>
        <p className="mb-5 text-sm text-muted-foreground">
          Markiert auffällige Buchungen in der Spalte „Verdacht" der Auswertung; der Filter „Nur Verdachtsfälle" zeigt sie
          gesammelt. Die Buchung selbst bleibt unverändert — ein Vorgesetzter sieht sie sich an und markiert sie als unbedenklich.
        </p>
        <div className="space-y-5">
          <SchalterZeile feld={verdacht} label="Verdachtsprüfung aktivieren" />
          {verdacht.wert && (
            <div className="ml-7 grid gap-4 sm:grid-cols-2">
              <Schwelle feld={kurz} id="vMin" label="Buchung kürzer als" einheit="Minuten" />
              <Schwelle feld={lang} id="vMax" label="Buchung länger als" einheit="Stunden" />
              <Schwelle feld={nachtVon} id="vVon" label="Ungewöhnliche Uhrzeit ab" einheit="Uhr" />
              <Schwelle feld={nachtBis} id="vBis" label="Ungewöhnliche Uhrzeit bis" einheit="Uhr" />
              {/* GPS-Schwellen nur mit gebuchtem GPS — ohne Koordinaten gibt es nichts zu vergleichen. */}
              {hatGps && <Schwelle feld={abweichung} id="vGps" label="Abweichung vom Stempelpunkt ab" einheit="Meter" />}
              {hatGps && <Schwelle feld={tempo} id="vKmh" label="Unmögliche Wege schneller als" einheit="km/h" />}
            </div>
          )}
        </div>
      </div>

      {/* ── Abschnitt 2: Bearbeitungserlaubnis ──────────────────────────────── */}
      <div className={formCard}>
        <h2 className={`mb-5 ${sectionTitle}`}>Bearbeitungserlaubnis für Zeitbuchungen</h2>
        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="editPolicySelect" className={fieldLabel}>
              Richtlinientyp
            </label>
            <div className="flex items-center gap-2">
              <select id="editPolicySelect" className={inputBase} {...richtlinie.props}>
                <option value="FREE">Frei (jederzeit bearbeitbar)</option>
                <option value="TIME_WINDOW">Zeitfenster (nur in bestimmtem Zeitraum)</option>
                <option value="WITH_NOTE">Mit Begründung (Notiz erforderlich)</option>
                <option value="LOCKED">Gesperrt (keine Bearbeitung nach Speichern)</option>
              </select>
              <FeldStatusZeichen status={richtlinie} />
            </div>
            <FeldStatus status={richtlinie} zeichen={false} />
          </div>

          {richtlinie.wert === 'TIME_WINDOW' && (
            <div className="space-y-2">
              <label htmlFor="editWindowHours" className={fieldLabel}>
                Bearbeitungsfenster (Stunden)
              </label>
              <div className="flex items-center gap-2">
                <input id="editWindowHours" inputMode="numeric" className={`${inputBase} max-w-[10rem]`} {...fenster.props} />
                <FeldStatusZeichen status={fenster} />
              </div>
              <FeldStatus status={fenster} zeichen={false} />
              <p className="text-xs text-muted-foreground">Anzahl der Stunden, in denen ein Eintrag noch bearbeitet werden kann.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Abschnitt 3: Standard-Felder Zeiterfassung ──────────────────────── */}
      <StandardFelder stelle={stelle} definitionen={definitionen} />

      {/* ── Abschnitt 4: Datenschutz ─────────────────────────────────────────── */}
      <div className={formCard}>
        <h2 className={`mb-5 ${sectionTitle}`}>Datenschutz</h2>
        <div className="space-y-2">
          <label htmlFor="privacyText" className={`${fieldLabel} flex items-center gap-1.5`}>
            Datenschutzhinweis <FeldStatusZeichen status={datenschutzText} />
          </label>
          <textarea
            id="privacyText"
            rows={5}
            placeholder="Gib einen Datenschutzhinweis ein, der deinen Mitarbeitern bei der Zeiterfassung angezeigt wird..."
            className={feldKlasse}
            {...datenschutzText.props}
          />
          <FeldStatus status={datenschutzText} zeichen={false} />
          <p className="text-xs text-muted-foreground">Dieser Text wird Mitarbeitern vor der ersten Zeiterfassung zur Bestätigung angezeigt.</p>
        </div>
        <div className="mt-4 space-y-2">
          <label htmlFor="privacyUrl" className={`${fieldLabel} flex items-center gap-1.5`}>
            Link zur Datenschutzerklärung <FeldStatusZeichen status={datenschutzLink} />
          </label>
          <input id="privacyUrl" type="url" placeholder="https://beispiel.at/datenschutz" className={feldKlasse} {...datenschutzLink.props} />
          <FeldStatus status={datenschutzLink} zeichen={false} />
          <p className="text-xs text-muted-foreground">
            Optional: URL zur vollständigen Datenschutzerklärung. Wird im Einwilligungs-Dialog als anklickbarer Link angezeigt.
          </p>
        </div>
      </div>
    </div>
  )
}

// ── Standard-Felder ────────────────────────────────────────────────────────

interface FeldEintrag {
  name: string
  visible: boolean
  required: boolean
}

/**
 * Die Standardfelder sind **eine** Liste (`standardFieldsConfig`). Jedes Häkchen
 * ist eine Operation auf dem aktuellen Stand (8.3 a) — so überschreibt ein
 * Häkchen nie das eines anderen Benutzers an einem anderen Feld. Die Hierarchie
 * (Projekt aus → Position und Schritt aus) steckt in der Operation.
 */
function StandardFelder({ stelle, definitionen }: { stelle: Speicherstelle<unknown>; definitionen: StandardFieldDefinition[] }) {
  const terms = useTerminology()
  const { t } = useTranslation()
  const hasModule = useAuthStore((s) => s.hasModule)
  const hatProjektzeit = hasModule('TIME_TRACKING.PROJECT_TIME')
  const gesperrtOhneProjektzeit = new Set(['projekt', 'projektposition', 'arbeitsschritt', 'taetigkeit'])
  const gesperrt = (name: string) => !hatProjektzeit && gesperrtOhneProjektzeit.has(name)

  /** Die volle Liste aus einem Stand — fehlende Einträge aus den Definitionen (der Server braucht alle). */
  const liste = (daten: Datensatz | undefined): FeldEintrag[] => {
    const roh = Array.isArray(daten?.standardFieldsConfig) ? (daten!.standardFieldsConfig as FeldEintrag[]) : []
    return definitionen.map((d) => {
      const g = roh.find((r) => r.name === d.name)
      return { name: d.name, visible: g ? g.visible : d.visible, required: g ? g.required : d.required }
    })
  }
  const jetzt = liste(stelle.daten)
  const wert = (name: string) => {
    if (gesperrt(name)) return { visible: false, required: false }
    return jetzt.find((f) => f.name === name) ?? { visible: false, required: false }
  }

  // Gesetzt wird der Wert, den der Benutzer angeklickt hat — nicht „umgedreht":
  // Auf einem inzwischen geänderten Stand höben sich zwei Klicks sonst auf.
  const umschalten = (name: string, was: 'visible' | 'required', an: boolean) => (aktuell: Datensatz) => {
    const felder = liste(aktuell)
    if (was === 'required') {
      return { standardFieldsConfig: felder.map((f) => (f.name === name && f.visible ? { ...f, required: an } : f)) }
    }
    const wirdVerborgen = !an
    const mitVerborgen = new Set<string>()
    if (wirdVerborgen && name === 'projekt') ['projektposition', 'arbeitsschritt'].forEach((n) => mitVerborgen.add(n))
    if (wirdVerborgen && name === 'projektposition') mitVerborgen.add('arbeitsschritt')
    return {
      standardFieldsConfig: felder.map((f) => {
        if (f.name === name) return { ...f, visible: an, required: wirdVerborgen ? false : f.required }
        if (mitVerborgen.has(f.name)) return { ...f, visible: false, required: false }
        return f
      }),
    }
  }

  const projektzeitFehlt = `Gehört zu „${t('modules.names.TIME_TRACKING.PROJECT_TIME')}" — für diesen Betrieb nicht gebucht`
  const projektSichtbar = wert('projekt').visible
  const positionSichtbar = wert('projektposition').visible
  const def = (name: string) => definitionen.find((d) => d.name === name)

  const zeilen: { name: string; label: string; sub?: string; aus?: boolean; grund?: string }[] = [
    { name: 'projekt', label: terms.project, sub: `enthält automatisch: ${terms.number} + ID${terms.project}`, aus: !hatProjektzeit, grund: projektzeitFehlt },
    {
      name: 'projektposition',
      label: terms.position,
      sub: `enthält automatisch: Nummer${terms.position} + ${terms.positionId}`,
      aus: !hatProjektzeit || !projektSichtbar,
      grund: hatProjektzeit ? `${terms.project} muss zuerst aktiviert sein` : projektzeitFehlt,
    },
    {
      name: 'arbeitsschritt',
      label: 'Arbeitsschritt',
      sub: 'enthält automatisch: IDArbeitsschritt',
      aus: !hatProjektzeit || !positionSichtbar,
      grund: hatProjektzeit ? `${terms.position} muss zuerst aktiviert sein` : projektzeitFehlt,
    },
    { name: 'taetigkeit', label: def('taetigkeit')?.label ?? 'Tätigkeit', aus: !hatProjektzeit, grund: projektzeitFehlt },
    // Die Betriebsstätte gehoert zum Kern, nicht zur Projektzeit.
    { name: 'betriebsstaette', label: def('betriebsstaette')?.label ?? 'Betriebsstätte' },
  ]

  return (
    <div className={formCard}>
      <div className="mb-5">
        <h2 className={sectionTitle}>Standard-Felder Zeiterfassung</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Vordefinierte Felder, die pro Zeitbuchung erfasst werden können.
          {` ${terms.position} setzt ${terms.project} voraus; Arbeitsschritt setzt ${terms.position} voraus.`}
        </p>
      </div>
      {definitionen.length === 0 ? (
        <p className="text-sm text-muted-foreground">Wird geladen…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <th className="pb-2 pr-4 font-medium">Feld</th>
                <th className="pb-2 pr-6 text-center font-medium">Sichtbar</th>
                <th className="pb-2 text-center font-medium">Pflichtfeld</th>
                <th className="w-6 pb-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {zeilen
                .filter((z) => def(z.name) && !def(z.name)!.isId)
                .map((z) => (
                  <StandardFeldZeile key={z.name} stelle={stelle} zeile={z} wert={wert(z.name)} umschalten={umschalten} />
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function StandardFeldZeile({
  stelle,
  zeile,
  wert,
  umschalten,
}: {
  stelle: Speicherstelle<unknown>
  zeile: { name: string; label: string; sub?: string; aus?: boolean; grund?: string }
  wert: { visible: boolean; required: boolean }
  umschalten: (name: string, was: 'visible' | 'required', an: boolean) => (aktuell: Datensatz) => Datensatz
}) {
  const aenderung = useAenderung(stelle, `standardFieldsConfig:${zeile.name}`)
  const sende = (was: 'visible' | 'required', an: boolean) =>
    void aenderung.sende({ felder: ['standardFieldsConfig'], operation: umschalten(zeile.name, was, an), labels: { standardFieldsConfig: zeile.label }, wert: was })
  return (
    <tr className={zeile.aus ? 'opacity-40' : ''}>
      <td className="py-3 pr-4">
        <p className="font-medium text-foreground">{zeile.label}</p>
        {zeile.sub && <p className="text-xs text-muted-foreground">{zeile.sub}</p>}
        {zeile.aus && zeile.grund && <p className="mt-0.5 text-xs italic text-muted-foreground">{zeile.grund}</p>}
        <FeldStatus status={aenderung.status} zeichen={false} />
      </td>
      <td className="py-3 pr-6 text-center">
        <input
          type="checkbox"
          aria-label={`${zeile.label} sichtbar`}
          checked={wert.visible}
          disabled={zeile.aus}
          onChange={(e) => sende('visible', e.target.checked)}
          className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-30"
        />
      </td>
      <td className="py-3 text-center">
        <input
          type="checkbox"
          aria-label={`${zeile.label} Pflichtfeld`}
          checked={wert.required}
          disabled={zeile.aus || !wert.visible}
          onChange={(e) => sende('required', e.target.checked)}
          className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-30"
        />
      </td>
      <td className="py-3">
        <FeldStatusZeichen status={aenderung.status} />
      </td>
    </tr>
  )
}
